import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { of, timer } from 'rxjs';
import { catchError, exhaustMap, switchMap, takeWhile, tap } from 'rxjs/operators';
import {
  YdsButtonBackComponent,
  YdsCardEditableComponent,
  YdsDropdownComponent,
  YdsSpinnerComponent,
  YdsTextFieldComponent,
  type YdsDropdownOption,
} from '../../../../yds-angular';
import { isInvalid } from '../../../../shared/general-ledger-yds.utils';
import {
  BookingGroup,
  CreateExportForGeneralledgerRequest,
  ExportDefinition,
  ExportProgress,
  ImportRunDetails,
  JournalProcessDefinition,
  PayrolljournalService,
} from '../payrolljournal.service';

const EXPORT_POLL_INTERVAL_MS = 2000;

@Component({
  selector: 'app-creategeneralledger',
  imports: [
    ReactiveFormsModule,
    YdsButtonBackComponent,
    YdsCardEditableComponent,
    YdsDropdownComponent,
    YdsSpinnerComponent,
    YdsTextFieldComponent,
  ],
  templateUrl: './creategeneralledger.component.html',
  styleUrl: './creategeneralledger.component.scss',
})
export class CreategeneralledgerComponent {
  private formBuilder = inject(FormBuilder);
  private payrolljournalService = inject(PayrolljournalService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  /** Guid of the import run this journal is created for. */
  readonly importRunId = signal<string>('');
  /** Name of the payroll client (opdrachtgever), passed through router navigation state. */
  readonly opdrachtgeverName = signal<string>('');
  /** Import run the journal is created for, loaded from the API. */
  private readonly importRun = signal<ImportRunDetails | null>(null);
  readonly instellingName = computed(() => this.importRun()?.institutionName ?? '');
  readonly periode = computed(() => {
    const run = this.importRun();
    return run ? `${run.year}/${String(run.period).padStart(2, '0')}` : '';
  });
  /** Selection on the previous page, restored when navigating back. */
  private opdrachtgeverId: string | null = null;
  private instellingId: string | null = null;

  readonly bookingGroups = signal<BookingGroup[]>([]);
  readonly journalProcessDefinitions = signal<JournalProcessDefinition[]>([]);
  readonly exportDefinitions = signal<ExportDefinition[]>([]);

  readonly bookingGroupOptions = computed<YdsDropdownOption[]>(() =>
    this.bookingGroups().map((group) => ({
      id: group.booking_Group_ID,
      label: group.bookingGroup_Name,
    })),
  );
  readonly journalProcessDefinitionOptions = computed<YdsDropdownOption[]>(() =>
    this.journalProcessDefinitions().map((definition) => ({
      id: definition.journalProcessDefinition_ID,
      label: definition.name,
    })),
  );
  readonly exportDefinitionOptions = computed<YdsDropdownOption[]>(() =>
    this.exportDefinitions().map((definition) => ({
      id: definition.exportDefinition_ID,
      label: definition.exportDefinition_Name,
    })),
  );

  /** The card opens directly in edit/add mode. */
  readonly detailsEditing = signal(true);
  readonly isSaving = signal(false);
  readonly isLoadingDefinitions = signal(false);
  readonly isInvalid = isInvalid;

  readonly submittedRequest = signal<CreateExportForGeneralledgerRequest | null>(null);
  readonly journalPlanningRecordId = signal<string>('');
  readonly startError = signal<string | null>(null);
  private readonly exportProgress = signal<ExportProgress | null>(null);
  readonly exportDone = computed(() => this.exportProgress()?.status === 'DONE');
  readonly exportFailed = computed(() => this.exportProgress()?.status === 'FAILED');
  readonly exportErrorMessage = computed(() => this.exportProgress()?.errorMessage ?? null);
  readonly isDownloading = signal(false);
  readonly downloadError = signal<string | null>(null);
  readonly isFileApiSend = computed(() => this.submittedRequest()?.sendTo === 'FILEAPI');

  readonly selectedBookingGroupName = computed(
    () =>
      this.bookingGroups().find((g) => g.booking_Group_ID === this.submittedRequest()?.bookingGroupId)
        ?.bookingGroup_Name ?? '',
  );
  readonly selectedJournalProcessDefinitionName = computed(
    () =>
      this.journalProcessDefinitions().find(
        (d) => d.journalProcessDefinition_ID === this.submittedRequest()?.journalProcessDefinitionId,
      )?.name ?? '',
  );
  readonly selectedExportDefinitionName = computed(
    () =>
      this.exportDefinitions().find(
        (d) => d.exportDefinition_ID === this.submittedRequest()?.exportDefinitionId,
      )?.exportDefinition_Name ?? '',
  );

  /** Only "Vervanging" is supported for now. */
  readonly soortJournalisering = 'Vervanging';

  readonly verzendingOptions: YdsDropdownOption[] = [
    { id: 'DIRECT', label: 'Direct download' },
    { id: 'FILEAPI', label: 'Sturen via file API' },
  ];

  readonly form: FormGroup = this.formBuilder.group({
    bookingGroupId: ['', Validators.required],
    journalProcessDefinitionId: ['', Validators.required],
    exportDefinitionId: ['', Validators.required],
    verzending: ['DIRECT', Validators.required],
  });

  readonly selectedVerzendingName = signal(
    this.verzendingOptions.find((o) => o.id === 'DIRECT')!.label,
  );

  constructor() {
    this.importRunId.set(this.route.snapshot.paramMap.get('importRunId') ?? '');

    const state = this.router.getCurrentNavigation()?.extras?.state ?? history.state;
    this.opdrachtgeverName.set((state?.['opdrachtgeverName'] as string) ?? '');
    this.opdrachtgeverId = (state?.['opdrachtgeverId'] as string) ?? null;
    this.instellingId = (state?.['instellingId'] as string) ?? null;

    this.payrolljournalService
      .getImportRun(this.importRunId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (importRun) => this.importRun.set(importRun),
        error: () => this.importRun.set(null),
      });

    this.form
      .get('bookingGroupId')!
      .valueChanges.pipe(
        tap((bookingGroupId: string) => {
          this.journalProcessDefinitions.set([]);
          this.form.patchValue({ journalProcessDefinitionId: '' });
          this.isLoadingDefinitions.set(!!bookingGroupId);
        }),
        // switchMap unsubscribes from the request of a previously selected booking group,
        // so a late response can never select a definition that does not belong to the
        // booking group currently in the form.
        switchMap((bookingGroupId: string) =>
          bookingGroupId
            ? this.payrolljournalService
                .getJournalProcessDefinitions(bookingGroupId)
                .pipe(catchError(() => of<JournalProcessDefinition[]>([])))
            : of<JournalProcessDefinition[]>([]),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((definitions) => {
        this.journalProcessDefinitions.set(definitions);
        if (definitions.length > 0) {
          this.form.patchValue({
            journalProcessDefinitionId: definitions[0].journalProcessDefinition_ID,
          });
        }
        this.isLoadingDefinitions.set(false);
      });

    this.payrolljournalService
      .getBookingGroups()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((groups) => {
        this.bookingGroups.set(groups);
        if (groups.length > 0) {
          this.form.patchValue({ bookingGroupId: groups[0].booking_Group_ID });
        }
      });

    this.payrolljournalService
      .getExportDefinitions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((definitions) => {
        this.exportDefinitions.set(definitions);
        if (definitions.length > 0) {
          this.form.patchValue({ exportDefinitionId: definitions[0].exportDefinition_ID });
        }
      });
  }

  goBack(): void {
    this.router.navigateByUrl('proces/export/exportgeneralledger/selectpayrolljournal', {
      state: { opdrachtgeverId: this.opdrachtgeverId, instellingId: this.instellingId },
    });
  }

  onCancel(): void {
    this.goBack();
  }

  onStartExport(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.value as {
      bookingGroupId: string;
      journalProcessDefinitionId: string;
      exportDefinitionId: string;
      verzending: string;
    };
    const request: CreateExportForGeneralledgerRequest = {
      importRunId: this.importRunId(),
      bookingGroupId: value.bookingGroupId,
      journalProcessDefinitionId: value.journalProcessDefinitionId,
      exportDefinitionId: value.exportDefinitionId,
      sendTo: value.verzending,
    };
    this.selectedVerzendingName.set(
      this.verzendingOptions.find((o) => o.id === value.verzending)?.label ?? '',
    );

    this.startError.set(null);
    this.isSaving.set(true);
    this.payrolljournalService
      .createExportForGeneralledger(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.submittedRequest.set(request);
          this.journalPlanningRecordId.set(result.journalPlanningRecordId);
          this.detailsEditing.set(false);
          this.isSaving.set(false);
          this.pollExportProgress(result.journalPlanningRecordId, result.jobProcessId);
        },
        error: () => {
          this.startError.set('Starten export mislukt.');
          this.isSaving.set(false);
        },
      });
  }

  onDownload(event: Event): void {
    event.preventDefault();
    if (this.isDownloading()) {
      return;
    }

    this.downloadError.set(null);
    this.isDownloading.set(true);
    this.payrolljournalService
      .downloadExportFile(this.journalPlanningRecordId(), this.exportProgress()?.fileName || 'Export')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.isDownloading.set(false),
        error: () => {
          this.downloadError.set('Download export mislukt.');
          this.isDownloading.set(false);
        },
      });
  }

  private pollExportProgress(journalPlanningRecordId: string, jobProcessId: string): void {
    timer(0, EXPORT_POLL_INTERVAL_MS)
      .pipe(
        // exhaustMap: ticks are ignored while a poll request is still in flight,
        // so slow responses are never cancelled.
        exhaustMap(() =>
          this.payrolljournalService.getExportProgress(journalPlanningRecordId, jobProcessId),
        ),
        takeWhile((progress) => progress.status === 'PENDING', true),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (progress) => this.exportProgress.set(progress),
        error: () =>
          this.exportProgress.set({ status: 'FAILED', fileName: '', errorMessage: null }),
      });
  }
}
